import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../../services/authentication.service';
import { ResetPassword } from 'src/app/models/ForgetPassword.model';
import { CloudNotificationStyle } from 'src/app/models/cloud-notification-style.model';
import { CloudNotificationService } from 'src/app/services/cloud-notification.service';
import Helper from 'src/app/Util/helper';
import { StringUtilities } from 'src/app/Util/string-utils';
import { Keys } from 'src/app/Util/keys';
import { AuthUtils } from 'src/app/Util/auth-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.sass']
})

export class ResetPasswordComponent implements OnInit, OnDestroy {

  private unsubscribe$ = new Subject<void>();
  resetPasswordFormGroup: FormGroup;

  code: string | undefined;
  action: string | undefined;
  encoded_email: string = ''
  encoded_resetCode: string = '';
  email: string = '';
  
  showCurrentPassword: boolean = false;
  showForm: boolean = false;
  isSubmittingResetForm = false;
  resetFormSubmitted = false;

  passwordShow: boolean = false;
  newPasswordShow: boolean = false;
  confirmPasswordShow: boolean = false;

  constructor(
    private readonly route: ActivatedRoute,
    private notificationSvc: CloudNotificationService,
    private authSvc: AuthenticationService,
    private router: Router, private formBuilder: FormBuilder
  ) {}

  ngOnInit() {

    this.getCodeFromQueryParams();

    if (!this.code) {
      this.navigateToLogin()
    }
    
    this.getEncodedEmailAndResetCode();
    this.parseAndAssignCredentials()

    if(!(this.action === Keys.update_password)) {
      this.checkIfResetLinkExpired();
    }

    if(this.action === Keys.update_password) {
      this.showCurrentPassword = true;
      this.showForm = true;
    }

    this.initializeResetPasswordForm()
  }

  ConfirmedValidator(password: string, confirmPassword: string) {
    return (formGroup: FormGroup) => {
      const Password = formGroup.controls[password];
      const ConfirmPassword = formGroup.controls[confirmPassword];
      if (ConfirmPassword.errors && !ConfirmPassword.errors.confirmedValidator) {
        return;
      }
      if (Password.value !== ConfirmPassword.value) {
        ConfirmPassword.setErrors({ confirmedValidator: true });
      } else {
        ConfirmPassword.setErrors(null);
      }
    }
  }

  UniquePasswordValidator(password: string, currentPassword: string, confirmPassword) {
    return (formGroup: FormGroup) => {
      const Password = formGroup.controls[password];
      const CurrentPassword = formGroup.controls[currentPassword];
      const ConfirmPassword = formGroup.controls[confirmPassword]
      if (CurrentPassword.errors && !CurrentPassword.errors.uniqueValidator)
        return;
      if (Password.value === CurrentPassword.value)
        Password.setErrors({ uniqueValidator: true });
       else if(Password.value != CurrentPassword.value){
        if(Password.errors != null){
          delete Password.errors['uniqueValidator']
        }
          if(Password.errors != null && Object.keys(Password.errors).length == 0){
            Password.setErrors(null);
          }
        }
      else if (Password.errors?.uniqueValidator)
        Password.setErrors({ uniqueValidator: false });
    }
  }

  onNewPasswordShowClick(passwordField: string) {
    if ('newPasswordShow' === passwordField) {
      this.newPasswordShow = !this.newPasswordShow;
    }
  } 
  
  onConfirmPasswordShowClick(passwordField: string) {
    if ('confirmPasswordShow' === passwordField) {
      this.confirmPasswordShow = !this.confirmPasswordShow;
    }
  }

  onPasswordShowClick(passwordField: string) {
    if ('passwordShow' === passwordField) {
      this.passwordShow = !this.passwordShow;
    }
  }

  get resetPassswordForm() { 
    return this.resetPasswordFormGroup.controls; 
  }

  onResetFormSubmission() {
    this.resetFormSubmitted = true;
    if (this.resetPasswordFormGroup.invalid) return;
    this.submitResetPassword();
  }

  private getCodeFromQueryParams() {
    this.route.queryParams.subscribe(params => {
      this.code = StringUtilities.replaceAllOccurrences(params.code, Keys.urlEncodedKey, Keys.equal) || undefined
      this.action = AuthUtils.decodeFromBase64(params.action) || undefined
    });
  }

  private getEncodedEmailAndResetCode(): void {
    const parts = this.code.split('.');
    this.encoded_email = parts[0] || '';
    this.encoded_resetCode = parts[1] || '';
  }

  private parseAndAssignCredentials(): void {
    this.email = AuthUtils.decodeFromBase64(StringUtilities.replaceAllOccurrences(this.encoded_email, Keys.urlEncodedKey, Keys.equal));
    this.encoded_resetCode = AuthUtils.decodeFromBase64(StringUtilities.replaceAllOccurrences(this.encoded_email, Keys.urlEncodedKey, Keys.equal));
  }

  private initializeResetPasswordForm(): void {
    this.resetPasswordFormGroup = this.formBuilder.group({
      current_password: [{ value: '', disabled: !this.showCurrentPassword }, Validators.required],
      password: ['', [Validators.required, Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[a-zA-Z\\d\\w\\W]{8,}$'),Validators.minLength(8),Validators.maxLength(100)]],
      confirm_password: ['', Validators.required]
    }, { validator: [this.ConfirmedValidator('password', 'confirm_password'), this.UniquePasswordValidator('password', 'current_password',"confirm_password")] })
  }
  
  private checkIfResetLinkExpired(): void {
    
    this.authSvc.is_reset_link_expired(this.code).pipe(takeUntil(this.unsubscribe$)).subscribe({

      next: (expired: boolean) => {
        if (expired) {
          this.showForm = true;
        }
      },

      error: (error: HttpErrorResponse) => {
        this.notificationSvc.createToastNotification('error ', Helper.resolveErrorMessage(error), CloudNotificationStyle.danger);
      }

    })
  }

  private submitResetPassword(): void {

    this.isSubmittingResetForm = true;
    const password = AuthUtils.encodeToBase64(this.resetPassswordForm.password.value);
    const oldPassword = this.action === Keys.update_password ? AuthUtils.encodeToBase64(this.resetPassswordForm.current_password.value) : null;

    this.authSvc.resetpassword(password, this.code, oldPassword).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (message) => {
        this.notificationSvc.createToastNotification('reset-password', message, CloudNotificationStyle.success);
        this.navigateToLogin();
      },

      error: (error: HttpErrorResponse) => {
        this.isSubmittingResetForm = false;
        this.notificationSvc.createToastNotification('reset-password-error ', Helper.resolveErrorMessage(error), CloudNotificationStyle.danger); 
      }
    })
  }

  navigateToLogin(e: Event = null): void {
    e?.preventDefault();
    this.router.navigate([environment.routes.login])
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  isUpdatePasswordAction(): boolean {
    return this.action === Keys.update_password;
  }
  
  isResetPasswordAction(): boolean {
    return !this.action;
  }

}
